import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, getErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send, Paperclip } from "lucide-react";
import { Footer } from "@/components/landing/Footer";
import { WaveIcon } from "@/components/landing/Decorations";

export default function PublicSupportPage() {
    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [subject, setSubject] = useState("");
    const [message, setMessage] = useState("");
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!fullName.trim() || !email.trim() || !subject.trim() || !message.trim()) {
            toast.error("Please fill in all required fields");
            return;
        }
        const fd = new FormData();
        fd.append("full_name", fullName.trim());
        fd.append("email", email.trim());
        fd.append("subject", subject.trim());
        fd.append("message", message.trim());
        Array.from(images).forEach((f) => fd.append("images", f));

        setLoading(true);
        try {
            await api.post("/support/public-ticket", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Your ticket has been submitted. We will contact you soon.");
            setSubject("");
            setMessage("");
            setImages([]);
        } catch (err) {
            toast.error(getErrorMessage(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-cream-100 text-ink">
            <header className="border-b border-cream-300 bg-white/80 backdrop-blur">
                <div className="mx-auto max-w-[1080px] px-5 sm:px-8 py-4 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <WaveIcon color="#FF5B3E" className="w-6 h-4"/>
                        <span className="font-display text-[22px] font-semibold tracking-tight">Audo</span>
                    </Link>
                    <div className="flex items-center gap-3 text-[13px]">
                        <Link to="/login" className="text-ink-muted hover:text-coral transition-colors">Login</Link>
                        <Link to="/register" className="bg-ink text-cream-100 rounded-full px-4 py-2 font-semibold hover:bg-coral transition-colors">Start free</Link>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-[940px] px-5 sm:px-8 py-10 sm:py-14">
                <p className="text-[11px] uppercase tracking-[0.2em] font-semibold text-coral mb-2">Support</p>
                <h1 className="font-display text-[40px] sm:text-[52px] leading-[1.05] font-semibold tracking-tight mb-3">Contact our support team</h1>
                <p className="text-[14px] text-ink-muted mb-8">Not logged in? No problem. Send us your issue and we will get back to you by email.</p>

                <form onSubmit={submit} className="bg-white rounded-3xl border border-cream-300 shadow-soft p-6 sm:p-8 space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            placeholder="Full name"
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                            required
                        />
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="Email"
                            className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                            required
                        />
                    </div>

                    <input
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                        placeholder="Subject"
                        className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                        required
                    />
                    <textarea
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                        placeholder="Describe your issue"
                        rows={6}
                        className="w-full border border-cream-300 rounded-xl px-3 py-2 text-[14px]"
                        required
                    />

                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <label className="text-[12px] text-ink-muted inline-flex items-center gap-2 cursor-pointer">
                            <Paperclip size={13}/> Add images
                            <input
                                type="file"
                                accept="image/*"
                                multiple
                                className="hidden"
                                onChange={(e) => setImages(e.target.files || [])}
                            />
                        </label>
                        <button
                            disabled={loading}
                            className="bg-ink hover:bg-coral text-cream-100 rounded-full px-5 py-2.5 text-[13px] font-semibold inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            {loading ? <Loader2 size={13} className="animate-spin"/> : <Send size={13}/>} Submit ticket
                        </button>
                    </div>
                </form>
            </main>

            <Footer/>
        </div>
    );
}
